import { PriceFeedData } from 'src/common/api-clients/swarm/types'
import { AnyToken } from 'src/common/types/models/Tokens'
import { unifyAddress } from 'src/common/utils/web3/unifyAddress'
import { TOKEN_PRICE_MAPPING, TOKENS_WITH_PRICE_FEEDS } from './consts'

// Utility function to check if a token has a price feed
export function hasPriceFeed(tokenAddress?: string, chainId?: number): boolean {
  if (!tokenAddress || !chainId) return false
  return TOKENS_WITH_PRICE_FEEDS[chainId].has(unifyAddress(tokenAddress))
}

export function createFormattedAddressSet(addresses: string[]): Set<string> {
  return new Set(addresses.map(unifyAddress))
}

export function getMappedTokenAddress(address: string): string {
  const unified = unifyAddress(address)
  return TOKEN_PRICE_MAPPING[unified] || unified
}

export function getPriceFeedForToken(
  priceFeeds?: Map<string, PriceFeedData>,
  address?: string
) {
  if (!priceFeeds || !address) return
  // Use the mapped token address for lookup.
  return priceFeeds.get(getMappedTokenAddress(address))
}

export function getPriceFromPriceFeed(
  priceFeeds?: Map<string, PriceFeedData>,
  address?: string
) {
  return getPriceFeedForToken(priceFeeds, address)?.price
}

export function injectSingleTokenPricingData(
  token: AnyToken,
  priceFeeds: Map<string, PriceFeedData> | undefined,
  callback?: (token: AnyToken) => AnyToken
) {
  const exchangePrice = getPriceFromPriceFeed(priceFeeds, token.contractAddress)

  const injectedToken = {
    ...token,
    usdBalance:
      (exchangePrice && token.balance && token.balance.mul(exchangePrice)) ||
      undefined,
    exchangePrice: exchangePrice,
  }

  return callback ? callback(injectedToken) : injectedToken
}

export function injectMultiTokenPricingData(
  tokens: AnyToken[],
  priceFeeds: Map<string, PriceFeedData> | undefined,
  callback?: (token: AnyToken) => AnyToken
) {
  return tokens.map(token =>
    injectSingleTokenPricingData(token, priceFeeds, callback)
  )
}

export function injectPricingData<T extends AnyToken | AnyToken[]>(
  tokens: T,
  priceFeeds: Map<string, PriceFeedData> | undefined,
  callback?: (token: AnyToken) => AnyToken
): T {
  if (Array.isArray(tokens)) {
    return injectMultiTokenPricingData(tokens, priceFeeds, callback) as T
  } else {
    return injectSingleTokenPricingData(tokens, priceFeeds, callback) as T
  }
}
